import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../components/api/ApiClient';
import { formatInvoiceSubject, parseInvoiceSubject } from '../../utils/invoiceUtils';
import moment from 'moment-jalaali';

import InitialInfoStep from './steps/InitialInfoStep';
import MainInfoStep from './steps/MainInfoStep';
import InvoiceTypeStep from './steps/InvoiceTypeStep';
import InvoiceAlgorithmStep from './steps/InvoiceAlgorithmStep';
import BuyerInfoStep from './steps/BuyerInfoStep';
import ItemsAndServicesStep from './steps/ItemsAndServicesStep';
import PaymentInfoStep from './steps/PaymentInfoStep';
import SubmitConfirmModal from './SubmitConfirmModal';
import ExcelUploadModal from './ExcelUploadModal';
import { jalaliToGregorian } from '../../utils/jalaliDate';

const toEnglishDigits = (str) => str.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));


const steps = [
    { name: 'اطلاعات اولیه صورتحساب', component: InitialInfoStep },
    { name: 'اطلاعات اصلی صورتحساب', component: MainInfoStep },
    { name: 'نوع صورتحساب', component: InvoiceTypeStep },
    { name: 'الگوی صورتحساب', component: InvoiceAlgorithmStep },
    { name: 'اطلاعات اولیه خریدار', component: BuyerInfoStep },
    { name: 'مشخصات کالا/خدمت', component: ItemsAndServicesStep },
    { name: 'اطلاعات پرداخت', component: PaymentInfoStep },
];

function InvoiceWizard() {
    const { id } = useParams();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        invoiceSubject: 'اصلی',
        invoiceStatus: 'First',
        invoiceNum: 1001,
        finalDate: (() => {
            return moment().format('HH:mm YYYY/MM/DD');
        })(),
        finalFaDate: (() => {
            return moment().format('HH:mm jYYYY/jMM/jDD');
        })(),
        storeNum: '',
        invoiceType: 'First_type',
        paymentType: 'cash',
        invoiceAlgo: 'First',
        buyerName: '',
        buyerType: 1,
        buyerBranchNum: '',
        buyerBranchName: '',
        buyerEconomicNum: '',
        buyerPostalCode: '',
        sellerContractId: '',
        buyerId: '',
        buyerAvailable: false,
        buyerPassport: '',
        flightType: '',
        economicAgencyNum: '',
        saleNoticeNum: '',
        saleNoticeDate: '',
        subId: '',
        contractingNumber: '',
        customsLicenseNumber: '',
        customSellerCode: '',
        productItems: [],
        cashPayment: '',
        creditPayment: '',
        paymentDate: '',
        paymentPrice: '',
        trackingNum: '',
        num17: '',
        paymentSwitch: '',
        terminalNum: '',
        paymentMethod: '',
        buyerCardNum: '',
        vppSum: '',
        sumPriceBeforeSale: 0,
        sumSalePrice: 0,
        sumPriceAfterSale: 0,
        sumTaxPrice: 0,
        sumOtherTax: 0,
        sumTotalPrice: 0,
        sumWeight: 0,
        sumRialValue: 0,
        sumCurrencyValue: 0,
        ladingBill: '',
        ladingBillType: '',
        ladingBillNum: '',
        originCountry: '',
        originCity: '',
        destinationCountry: '',
        destinationCity: '',
        senderNationalCode: '',
        receiverNationalCode: '',
        shippingNum: '',
        driverNationalCode: '',
        shippingProducts: [],
    });

    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [showBuyerModal, setShowBuyerModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [pendingExit, setPendingExit] = useState(false);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [isInvoiceTypeDisabled, setIsInvoiceTypeDisabled] = useState({
        algo: false,
        payment: false,
    });
    const [userConfig, setUserConfig] = useState({});

    const mode = useMemo(() => {
        if (location.pathname.includes('/view/')) return 'view';
        if (location.pathname.includes('/edit/')) return 'edit';
        return 'create';
    }, [location.pathname]);

    const readOnly = mode === 'view';

    const [branches, setBranches] = useState([]);

    const getInvoiceConfig = async (typeOfInvoice, algoOfInvoice) => {
        try {
            const response = await apiClient('/user-preferences/api/invoice-config/', {
                params: {
                    typeOfInvoice,
                    algoOfInvoice
                }
            });
            let fieldsArray = response?.fields_config || response;

            if (!Array.isArray(fieldsArray) && fieldsArray && typeof fieldsArray === 'object') {
                fieldsArray = Object.entries(fieldsArray).map(
                    ([name, cfg]) => ({ name, ...cfg })
                );
            }
            
            if (Array.isArray(fieldsArray)) {
                const configObject = Object.fromEntries(
                    fieldsArray.map(({ name, ...rest }) => [name, rest])
                );
                setUserConfig(configObject);
            }
        } catch (e) {
            console.error("Error fetching invoice config:", e);
        }
    };

    const isRequired = useCallback(
        (fieldName) => {
            if (formData.invoiceType === 'Second_type' && [
                'buyerType', 'buyerPostalCode', 'buyerEconomicNum', 'buyerBranchNum',
                'buyerBranchName', 'buyerPassport', 'economicAgencyNum', 'saleNoticeNum',
                'saleNoticeDate', 'sellerContractId', 'subId', 'flightType'
            ].includes(fieldName)) {
                return false;
            }
            const field = userConfig?.[fieldName];
            return field ? (field.active && field.required) : false;
        },
        [userConfig, formData.invoiceType]
    );

    const isShow = useCallback(
        (fieldName) => userConfig?.[fieldName]?.active ?? true,
        [userConfig]
    );

    useEffect(() => {
        if (formData.invoiceType && formData.invoiceAlgo) {
            getInvoiceConfig(formData.invoiceType, formData.invoiceAlgo);
        }
    }, [formData.invoiceType, formData.invoiceAlgo]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await apiClient('/invoice/api/new-invoice/');
                
                let num = 1001;
                let branchesData = [];

                if (response) {
                    num = response.num || num;
                    branchesData = response.branches || [];
                }

                setBranches(branchesData);
                if (mode === 'create') {
                    setFormData(prev => ({
                        ...prev,
                        invoiceNum: num,
                        storeNum: branchesData.length > 0 ? branchesData[0].taxMemoryId : prev.storeNum,
                        buyerAvailable: false
                    }));
                }
            } catch (error) {
                console.error("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, [mode]);

    useEffect(() => {
        if ((mode === 'edit' || mode === 'view') && id) {
            const fetchInvoice = async () => {
                try {
                    const response = await apiClient('/invoice/api/', {
                        method: 'POST',
                        data: { invoiceId: parseInt(id, 10) },
                    });
                    const invoiceData = response.data;

                    let fetchedStoreNum = invoiceData.storeNum;
                    if (!fetchedStoreNum && invoiceData.branch) {
                         if (typeof invoiceData.branch === 'object') {
                              fetchedStoreNum = invoiceData.branch.taxMemoryId || invoiceData.branch.code || invoiceData.branch.id;
                         } else {
                              fetchedStoreNum = invoiceData.branch;
                         }
                    }

                    let fetchedFinalDate = '';
                    let fetchedFinalFaDate = '';
                    
                    if (mode === 'view') {
                        fetchedFinalDate = invoiceData.finalDate || formData.finalDate;
                        fetchedFinalFaDate = formData.finalFaDate;
                        
                        if (invoiceData.finalDate) {
                             const timeFirstFormat = 'HH:mm YYYY/MM/DD';
                             let m = moment(invoiceData.finalDate, timeFirstFormat);
                             if (!m.isValid()) m = moment(invoiceData.finalDate);
                             
                             if (m.isValid()) {
                                 if (m.year() < 1800) {
                                      const mJ = moment(invoiceData.finalDate, 'HH:mm jYYYY/jMM/jDD');
                                      if (mJ.isValid()) m = mJ;
                                 }
                                 fetchedFinalDate = m.format(timeFirstFormat);
                                 fetchedFinalFaDate = m.format('HH:mm jYYYY/jMM/jDD');
                             }
                        }
                        
                    } else if (mode === 'edit') {
                        fetchedFinalDate = invoiceData.finalDate || formData.finalDate;
                        fetchedFinalFaDate = fetchedFinalDate || formData.finalFaDate;
                        
                        const dateLastPattern = /^(\d{4}\/\d{2}\/\d{2})\s(\d{2}:\d{2})$/;
                        const match = fetchedFinalFaDate?.match(dateLastPattern);
                        console.log(match)
                        if (match) {
                            fetchedFinalFaDate = `${match[2]} ${match[1]}`;
                            const _time = `${match[2]}`;
                            const formatDate = match[1].split('/');
                            const _date = jalaliToGregorian(formatDate[0], formatDate[1], formatDate[2]);
                            fetchedFinalDate = `${_time} ${_date.year}/${_date.month}/${_date.day}`
                        }
                    }
                    setFormData(prev => ({
                        ...prev,
                        invoiceSubject: formatInvoiceSubject(invoiceData.invoiceSubject),
                        invoiceStatus: invoiceData.invoiceStatus || prev.invoiceStatus,
                        invoiceNum: invoiceData.invoiceNum || prev.invoiceNum,
                        finalDate: fetchedFinalDate,
                        finalFaDate: fetchedFinalFaDate,
                        storeNum: fetchedStoreNum || prev.storeNum,
                        invoiceType: invoiceData.invoiceType || prev.invoiceType,
                        paymentType: invoiceData.paymentType || prev.paymentType,
                        invoiceAlgo: invoiceData.invoiceAlgo || prev.invoiceAlgo,
                        buyerName: invoiceData.buyerName || (invoiceData.buyer ? invoiceData.buyer.name : '') || '',
                        buyerType: parseInt(invoiceData.buyerType) || prev.buyerType,
                        buyerBranchNum: invoiceData.buyerBranchNum || '',
                        buyerBranchName: invoiceData.buyerBranchName || '',
                        buyerEconomicNum: invoiceData.buyerEconomicNum || (invoiceData.buyer ? invoiceData.buyer.economicCode : '') || '',
                        buyerPostalCode: invoiceData.buyerPostalCode || (invoiceData.buyer ? invoiceData.buyer.postalCode : '') || '',
                        buyerNationalCode: invoiceData.buyerNationalCode || '',
                        sellerContractId: invoiceData.sellerContractId || '',
                        buyerId: String(invoiceData.buyerId || (invoiceData.buyer ? (invoiceData.buyer.uuid || invoiceData.buyer.UUID) : '') || ''),
                        buyerAvailable: invoiceData.buyerAvailable || (!!invoiceData.buyerId || !!invoiceData.buyer),
                        buyerPassport: invoiceData.buyerPassport || '',
                        flightType: invoiceData.flightType || '',
                        economicAgencyNum: invoiceData.economicAgencyNum || '',
                        saleNoticeNum: invoiceData.saleNoticeNum || '',
                        saleNoticeDate: invoiceData.saleNoticeDate || '',
                        subId: invoiceData.subId || '',
                        contractingNumber: invoiceData.contractingNumber || '',
                        customsLicenseNumber: invoiceData.customsLicenseNumber || '',
                        customSellerCode: invoiceData.customSellerCode || '',
                        productItems: (invoiceData.productItems || []).map(item => {
                            const qty = item.amount ?? item.count;
                            const qtyNum = parseFloat(qty) || 0;
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const salePrice = parseFloat(item.salePrice) || 0;
                            const priceBeforeSale = item.priceBeforeSale ?? (qtyNum * unitPrice);
                            const priceAfterSale = item.priceAfterSale ?? (priceBeforeSale - salePrice);
                            const taxPrice = item.taxPrice ?? (priceAfterSale * ((parseFloat(item.taxPercent) || 0) / 100));
                            const totalPrice = item.totalPrice ?? (priceAfterSale + taxPrice + (item.otherTaxP || 0) + (item.otherLegalP || 0));
                            return {
                                id: item.productId,
                                code: item.productId,
                                productId: item.productId,
                                name: item.name,
                                count: null,
                                amount: String(qty ?? ''),
                                unitPrice: item.unitPrice,
                                salePrice: salePrice,
                                taxPercent: item.taxPercent,
                                priceBeforeSale,
                                priceAfterSale,
                                taxPrice,
                                otherTax: item.otherTaxP || 0,
                                otherLegalFunds: item.otherLegalP || 0,
                                totalPrice,
                                measurement: item.measurement,
                                currencyType: item.currency || 'IRR',
                            };
                        }),
                        cashPayment: invoiceData.cashPayment !== undefined ? invoiceData.cashPayment : '',
                        creditPayment: invoiceData.creditPayment !== undefined ? invoiceData.creditPayment : '',
                        paymentDate: invoiceData.paymentDate || '',
                        paymentPrice: invoiceData.paymentPrice || '',
                        trackingNum: invoiceData.trackingNum || '',
                        num17: invoiceData.num17 || '',
                        paymentSwitch: invoiceData.paymentSwitch || '',
                        terminalNum: invoiceData.terminalNum || '',
                        paymentMethod: invoiceData.paymentMethod || '',
                        buyerCardNum: invoiceData.buyerCardNum || '',
                        vppSum: invoiceData.vppSum !== undefined ? invoiceData.vppSum : '',
                        sumPriceBeforeSale: invoiceData.sumPriceBeforeSale || 0,
                        sumSalePrice: invoiceData.sumSalePrice || 0,
                        sumPriceAfterSale: invoiceData.sumPriceAfterSale || 0,
                        sumTaxPrice: invoiceData.sumTaxPrice || 0,
                        sumOtherTax: invoiceData.sumOtherTax || 0,
                        sumTotalPrice: invoiceData.sumTotalPrice || 0,
                        ladingBill: invoiceData.ladingBill || '',
                        ladingBillType: invoiceData.ladingBillType || '',
                        ladingBillNum: invoiceData.ladingBillNum || '',
                        originCountry: invoiceData.originCountry || '',
                        originCity: invoiceData.originCity || '',
                        destinationCountry: invoiceData.destinationCountry || '',
                        destinationCity: invoiceData.destinationCity || '',
                        senderNationalCode: invoiceData.senderNationalCode || '',
                        receiverNationalCode: invoiceData.receiverNationalCode || '',
                        shippingNum: invoiceData.shippingNum || '',
                        driverNationalCode: invoiceData.driverNationalCode || '',
                        shippingProducts: invoiceData.shippingProductsRead || invoiceData.shippingProducts || [],
                    }));
                } catch (error) {
                    console.error("Error fetching invoice:", error);
                    setSubmitError("خطا در دریافت اطلاعات صورتحساب.");
                }
            };
            fetchInvoice();
        }
    }, [mode, id]);

    useEffect(() => {
        if (branches.length > 0) {
            const isTaxId = branches.some(b => b.taxMemoryId === formData.storeNum);
            
            if (!isTaxId) {
                const branchById = branches.find(b => b.id === formData.storeNum || b.id === Number(formData.storeNum));
                
                if (branchById) {
                     setFormData(prev => ({ ...prev, storeNum: branchById.taxMemoryId }));
                } else {
                     if (!readOnly && mode !== 'view') {
                          setFormData(prev => ({ ...prev, storeNum: branches[0].taxMemoryId || branches[0].id }));
                     }
                }
            }
        }
    }, [branches, formData.storeNum, mode, readOnly]);


    const handleDataChange = useCallback((stepData) => {
        if (readOnly) return;

        if (stepData.finalDate) {
             const faDateStr = toEnglishDigits(String(stepData.finalDate));
             const m = moment(faDateStr, 'HH:mm jYYYY/jMM/jDD');
             
             if (m.isValid()) {
                 const now = moment();
                 if (m.isAfter(now)) {
                      setValidationError('تاریخ انتخاب شده نباید از زمان حال بزرگتر باشد!');
                      return; 
                 } else {
                     setValidationError(null);
                 }

                 const enDateStr = m.format('HH:mm YYYY/MM/DD');
                 setFormData(prev => ({ 
                     ...prev, 
                     finalFaDate: faDateStr, 
                     finalDate: enDateStr 
                 }));
             }
        } else {
            setFormData(prevData => ({ ...prevData, ...stepData }));
        }
    }, [readOnly]);

    const calculatedTotals = useMemo(() => {
            const items = formData.productItems || [];

            const sumPriceBeforeSale = Math.max(0, items.reduce((sum, item) => {
                return sum + Math.max(0, parseFloat(item.priceBeforeSale) || 0);
            }, 0));

            const sumSalePrice = Math.max(0, items.reduce((sum, item) => {
                return sum + Math.max(0, parseFloat(item.salePrice) || 0);
            }, 0));

            const actualSalePrice = Math.min(sumSalePrice, sumPriceBeforeSale);
            const sumPriceAfterSale = Math.max(0, sumPriceBeforeSale - actualSalePrice);

            const sumTaxPrice = Math.max(0, items.reduce((sum, item) => {
                return sum + Math.max(0, parseFloat(item.taxPrice) || 0);
            }, 0));

            const sumOtherTax = Math.max(0, items.reduce((sum, item) => {
                return sum + Math.max(0, parseFloat(item.otherTax) || 0);
            }, 0));

            const sumOtherLegalFunds = Math.max(0, items.reduce((sum, item) => {
                return sum + Math.max(0, parseFloat(item.otherLegalFunds) || 0);
            }, 0));

            const sumTotalPrice = Math.max(0, sumPriceAfterSale + sumTaxPrice + sumOtherTax + sumOtherLegalFunds);

            const sumWeight = items.reduce((sum, item) => {
                return sum + (parseFloat(item.weight) || 0);
            }, 0);

            const sumRialValue = items.reduce((sum, item) => {
                return sum + (parseFloat(item.rialValue) || 0);
            }, 0);

            const sumCurrencyValue = items.reduce((sum, item) => {
                return sum + (parseFloat(item.currencyValue) || 0);
            }, 0);

            const vppValue = items.reduce((sum, item) => {
                return sum + (parseFloat(item.vpp) || 0);
            }, 0);

            return {
                sumPriceBeforeSale: Math.max(0, Math.trunc(sumPriceBeforeSale)),
                sumSalePrice: Math.max(0, Math.trunc(actualSalePrice)),
                sumPriceAfterSale: Math.max(0, Math.trunc(sumPriceAfterSale)),
                sumTaxPrice: Math.max(0, Math.trunc(sumTaxPrice)),
                sumOtherTax: Math.max(0, Math.trunc(sumOtherTax)),
                sumOtherLegalFunds: Math.max(0, Math.trunc(sumOtherLegalFunds)),
                sumTotalPrice: Math.max(0, Math.trunc(sumTotalPrice)),
                sumWeight: sumWeight,
                sumRialValue: Math.trunc(sumRialValue),
                sumCurrencyValue: Math.trunc(sumCurrencyValue),
                vppSum: Math.trunc(vppValue),
            };
        }, [formData.productItems]);

    const validateStep = () => {
        setValidationError(null);

        if (readOnly) {
            return true;
        }

        const scrollToTop = () => {
             window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        const data = formData;

        if (currentStep === 1) {
            if (!data.invoiceNum || !data.finalDate || (!data.storeNum && data.storeNum !== 0)) {
                setValidationError('فیلدهای شماره صورتحساب، تاریخ و شعبه اصلی اجباری هستند.');
                scrollToTop();
                return false;
            }
            if (data.finalFaDate) {
                 const m = moment(data.finalFaDate, 'HH:mm jYYYY/jMM/jDD');
                 if (m.isValid() && m.isAfter(moment())) {
                      setValidationError('تاریخ انتخاب شده نباید از زمان حال بزرگتر باشد!');
                      scrollToTop();
                      return false;
                 }
            }
        }

        if (currentStep === 4) {
            if (data.invoiceType === 'Second_type') {
                return true;
            }
            const buyerType = parseInt(data.buyerType) || 1;
            if (buyerType === 1 || buyerType === 4) {
                if (isRequired('buyerType') && !data.buyerId && !data.personId) {
                    setValidationError('شناسه/کد ملی خریدار الزامی است.');
                    scrollToTop();
                    return false;
                }
                if (isRequired('buyerPostalCode') && !data.buyerPostalCode) {
                    setValidationError('کد پستی خریدار الزامی است.');
                    scrollToTop();
                    return false;
                }
            } else if (buyerType === 2 || buyerType === 3) {
                if (isRequired('buyerEconomicNum') && !data.buyerEconomicNum) {
                    setValidationError('شماره اقتصادی خریدار الزامی است.');
                    scrollToTop();
                    return false;
                }
            }
        }

        if (currentStep === 5 && (!data.productItems || data.productItems.length === 0)) {
            setValidationError('افزودن حداقل یک کالا یا خدمت اجباری است.');
             scrollToTop();
            return false;
        }

        if (currentStep === 6) {
            if (data.paymentType !== 'credit' && isRequired('cashPayment') && (!data.cashPayment || isNaN(data.cashPayment) || parseFloat(data.cashPayment) < 0)) {
                setValidationError('مبلغ پرداختی نقدی اجباری است و باید عددی غیر منفی باشد.');
                 scrollToTop();
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (currentStep < steps.length - 1) {
                setCurrentStep(prev => prev + 1);
            }
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            setValidationError(null);
        }
    };

    const handleSubmit = (exit = false) => {
        if (readOnly) return;
        if (!validateStep()) return;
        setPendingExit(exit);
        setShowSubmitModal(true);
    };

    const handleConfirmSubmit = async (sendToMoadian) => {
        setShowSubmitModal(false);
        setIsSubmitting(true);
        setSubmitError(null);

        const toNull = (val) => (val === '' || val === undefined ? null : val);
        const isUUID = (val) => val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
        const getCommonPayload = () => ({
            invoiceNum: parseInt(formData.invoiceNum),
            buyerAvailable: formData.buyerAvailable !== undefined ? formData.buyerAvailable : isUUID(formData.buyerId || formData.personId),
            buyerName: formData.buyerName || null,
            paymentType: formData.paymentType,
            invoiceType: formData.invoiceType,
            invoiceAlgo: formData.invoiceAlgo,
            invoiceSubject: parseInvoiceSubject(formData.invoiceSubject),
            createDate: null, 
            finalDate: formData.finalDate || null,
            branch: formData.storeNum,
            
            buyerType: parseInt(formData.buyerType) || 1,

            buyerId: formData.buyerId || formData.personId || null,
            buyerPostalCode: toNull(formData.buyerPostalCode),
            subId: toNull(formData.subId),
            sellerContractId: toNull(formData.sellerContractId),
            buyerEconomicNum: toNull(formData.buyerEconomicNum || formData.personId || (!isUUID(formData.buyerId) ? formData.buyerId : null)),
            buyerBranchNum: toNull(formData.buyerBranchNum),
            buyerBranchName: toNull(formData.buyerBranchName),
            buyerPassport: toNull(formData.buyerPassport),
            flightType: toNull(formData.flightType),
            economicAgencyNum: toNull(formData.economicAgencyNum),
            saleNoticeNum: toNull(formData.saleNoticeNum),
            saleNoticeDate: toNull(formData.saleNoticeDate),
            contractingNumber: toNull(formData.contractingNumber),
            customsLicenseNumber: toNull(formData.customsLicenseNumber),
            customSellerCode: toNull(formData.customSellerCode),

            productItems: formData.productItems.map((item, index) => ({
                productId: item.productId || item.code,
                name: item.name,
                measurement: item.measurement,
                count: null,
                amount: item.amount || item.count || '0',
                weight: item.weight !== undefined && item.weight !== '' && item.weight !== null ? parseFloat(item.weight) : null,
                currency: item.currencyType || "IRR",
                unitPrice: parseFloat(item.unitPrice),
                currencyAmount: item.currencyAmount !== undefined && item.currencyAmount !== '' && item.currencyAmount !== null ? parseFloat(item.currencyAmount) : null,
                currencyPrice: item.currencyPrice !== undefined && item.currencyPrice !== '' && item.currencyPrice !== null ? parseFloat(item.currencyPrice) : null,
                rialValue: item.rialValue !== undefined && item.rialValue !== '' && item.rialValue !== null ? parseFloat(item.rialValue) : null,
                currencyValue: item.currencyValue !== undefined && item.currencyValue !== '' && item.currencyValue !== null ? parseFloat(item.currencyValue) : null,
                priceBeforeSale: parseFloat(item.priceBeforeSale),
                salePrice: parseFloat(item.salePrice || 0),
                priceAfterSale: parseFloat(item.priceAfterSale),
                taxPercent: parseFloat(item.taxPercent),
                taxPrice: parseFloat(item.taxPrice),
                otherTaxS: item.otherTaxS || "", 
                otherTaxR: item.otherTaxR || "",
                otherTaxP: parseFloat(item.otherTax || item.otherTaxP || 0),
                otherLegalS: item.otherLegalS || "",
                otherLegalR: item.otherLegalR || "",
                otherLegalP: parseFloat(item.otherLegalFunds || item.otherLegalP || 0),
                constructionFee: item.constructionFee !== undefined && item.constructionFee !== '' && item.constructionFee !== null ? parseFloat(item.constructionFee) : null,
                sellerProfit: item.sellerProfit !== undefined && item.sellerProfit !== '' && item.sellerProfit !== null ? parseFloat(item.sellerProfit) : null,
                commission: item.commission !== undefined && item.commission !== '' && item.commission !== null ? parseFloat(item.commission) : null,
                sumCSC: parseFloat(item.sumCSC) || 0,
                constructionFeeNum: item.constructionFeeNum || "",
                cashContribution: parseFloat(item.cashContribution) || parseFloat(item.totalPrice) || 0,
                vpp: (() => {
                    const taxP = parseFloat(item.taxPrice) || 0;
                    const cashC = parseFloat(item.cashContribution) || parseFloat(item.totalPrice) || 0;
                    const priceAS = parseFloat(item.priceAfterSale) || 1;
                    if (priceAS === 0) return taxP;
                    return Math.trunc((taxP * cashC) / priceAS);
                })(),
                totalPrice: parseFloat(item.totalPrice) || 0,
                diffRialCurrency: item.diffRialCurrency !== undefined && item.diffRialCurrency !== '' && item.diffRialCurrency !== null ? parseFloat(item.diffRialCurrency) : null,
                carat: item.carat !== undefined && item.carat !== '' && item.carat !== null ? parseFloat(item.carat) : null,
                taxSource: item.taxSource || null,
                index: index
            })),

            cashPayment: parseFloat(formData.cashPayment || 0),
            creditPayment: parseFloat(formData.creditPayment || 0),
            paymentDate: toNull(formData.paymentDate),
            paymentPrice: toNull(formData.paymentPrice),
            trackingNum: toNull(formData.trackingNum),
            num17: toNull(formData.num17),
            paymentSwitch: toNull(formData.paymentSwitch),
            storeNum: toNull(formData.storeNum),
            terminalNum: toNull(formData.terminalNum),
            paymentMethod: toNull(formData.paymentMethod),
            buyerCardNum: toNull(formData.buyerCardNum),
            vppSum: parseFloat(formData.vppSum || 0),
            sumWeight: calculatedTotals.sumWeight || null,
            sumPriceBeforeSale: calculatedTotals.sumPriceBeforeSale,
            sumSalePrice: calculatedTotals.sumSalePrice,
            sumPriceAfterSale: calculatedTotals.sumPriceAfterSale,
            sumTaxPrice: calculatedTotals.sumTaxPrice,
            sumOtherTax: calculatedTotals.sumOtherTax,
            sumTotalPrice: calculatedTotals.sumTotalPrice,
            sumRialValue: calculatedTotals.sumRialValue || 0,
            sumCurrencyValue: calculatedTotals.sumCurrencyValue || 0,
            shippingProducts: formData.shippingProducts || [],
            ladingBill: toNull(formData.ladingBill),
            ladingBillType: toNull(formData.ladingBillType),
            ladingBillNum: toNull(formData.ladingBillNum),
            originCountry: toNull(formData.originCountry),
            originCity: toNull(formData.originCity),
            destinationCountry: toNull(formData.destinationCountry),
            destinationCity: toNull(formData.destinationCity),
            senderNationalCode: toNull(formData.senderNationalCode),
            receiverNationalCode: toNull(formData.receiverNationalCode),
            shippingNum: toNull(formData.shippingNum),
            driverNationalCode: toNull(formData.driverNationalCode),
        });

        try {
            let result;
            if (mode === 'edit') {
                const editPayload = getCommonPayload();
                editPayload.id = parseInt(id);
                editPayload.invoiceId = parseInt(id);
                editPayload.sendToMoadian = sendToMoadian;
                editPayload.buyerAvailable = false;
                editPayload.buyerId = formData.buyerNationalCode ? formData.buyerNationalCode : formData.buyerId;

                result = await apiClient('/invoice/api/', {
                    method: 'PUT',
                    data: editPayload,
                });
                setToast({ show: true, message: 'تغییرات با موفقیت ذخیره شد!', type: 'success' });
                setTimeout(() => navigate('/invoices/list'), 1500);
            } else {
                const commonData = getCommonPayload();

                const finalBody = {
                    payload: commonData,
                    sendToMoadian: sendToMoadian
                };

                result = await apiClient('/invoice/api/new-invoice/', {
                    method: 'POST',
                    data: finalBody,
                });
                setToast({ show: true, message: 'صورتحساب با موفقیت ثبت شد!', type: 'success' });
                setTimeout(() => navigate('/invoices/list/'), 2000);
            }

        } catch (error) {
            console.error("Submit Error:", error);
            setSubmitError(error.message || "خطا در ارسال نهایی صورتحساب.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const CurrentStepComponent = steps[currentStep].component;

    const stepProps = {
        formData: {
            ...formData,
            finalDate: formData.finalFaDate
        },
        onDataChange: handleDataChange,
        calculatedTotals,
        setIsInvoiceTypeDisabled,
        isInvoiceTypeDisabled,
        setShowBuyerModal,
        showBuyerModal,
        readOnly,
        branches,
        mode,
        isShow,
        isRequired,
    };

    const renderStepper = () => (
        <div className="card mb-4">
            <div className="card-header border-0">
                <h6 className="fw-bold mb-2">
                    <i className="feather-bookmark me-2 text-primary"></i>
                    {mode === 'view' ? 'مشاهده صورتحساب' : (mode === 'edit' ? 'ویرایش صورتحساب' : 'مراحل صدور فاکتور')}
                </h6>
            </div>
            <div className="card-body p-0" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}>
                <ul className="nav flex-column nxl-wizard-vertical">
                    {steps.map((step, index) => {
                        const isDone = index < currentStep;
                        const isActive = index === currentStep;

                        const iconClass = isDone ? 'feather-check-circle' : 'feather-disc';
                        const iconColor = isDone ? 'text-success' : isActive ? 'text-primary' : 'text-muted';

                        return (
                            <li key={index} className="nav-item mb-2">
                                <a
                                    className={`nav-link ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} d-flex`}
                                    href="#"
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <div className="nav-step">
                                        <i className={`feather ${iconClass} ${iconColor} me-2`}></i>
                                    </div>
                                    <span className="fw-bold fs-14 me-auto">{step.name}</span>
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );

    const downloadTestExcel = () => {
        var filename = 'excelTest.xlsx';
        const link = document.createElement("a");
        link.href = `/assets/files/${filename}`;
        link.download = "نمونه صدور با اکسل.xlsx";
        link.click();
    }

    const [excelBtnContainer, setExcelBtnContainer] = useState(null);

    useEffect(() => {
        if (mode !== 'create') return;

        const pageHeader = document.querySelector('.page-header');
        if (!pageHeader) return;

        let rightSection = pageHeader.querySelector('.page-header-right.ms-auto');
        if (!rightSection) {
            rightSection = document.createElement('div');
            rightSection.className = 'page-header-right ms-auto';
            pageHeader.appendChild(rightSection);
        }

        const container = document.createElement('div');
        container.className = 'd-flex align-items-center gap-2 flex-wrap';
        rightSection.prepend(container);
        setExcelBtnContainer(container);

        return () => {
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
            setExcelBtnContainer(null);
        };
    }, [mode]);

    return (
        <>
            {mode === 'create' && excelBtnContainer && createPortal(
                <button
                    className="btn btn-success d-flex align-items-center"
                    onClick={() => setShowExcelModal(true)}
                    title="صدور صورتحساب با اکسل"
                >
                    <i className="feather-file-text me-0 me-md-2"></i>
                    <span className="d-none d-md-inline">صدور صورتحساب با اکسل</span>
                </button>,
                excelBtnContainer
            )}
            {mode === 'create' && excelBtnContainer && createPortal(
                <button
                    className="btn btn-success d-flex align-items-center"
                    onClick={() => downloadTestExcel()}
                    title="نمونه اکسل برای صدور صورتحساب"
                >
                    <i className="feather-file me-0 me-md-2"></i>
                    <span className="d-none d-md-inline">نمونه اکسل برای صدور صورتحساب</span>
                </button>,
                excelBtnContainer
            )}
            <div className="row">

            {/* Mobile horizontal progress bar - visible only on small screens */}
            <div className="col-12 d-md-none mb-3">
                <div className="card">
                    <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-bold fs-13 text-primary">
                                <i className="feather-bookmark me-1"></i>
                                {mode === 'view' ? 'مشاهده' : (mode === 'edit' ? 'ویرایش' : 'صدور')}
                            </span>
                            <span className="badge bg-primary rounded-pill">
                                {`${currentStep + 1} / ${steps.length}`}
                            </span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                            <div
                                className="progress-bar bg-primary"
                                role="progressbar"
                                style={{ width: `${((currentStep + 1) / steps.length) * 100}%`, transition: 'width 0.4s ease' }}
                            />
                        </div>
                        <div className="text-muted fs-12 mt-1 text-center">
                            {steps[currentStep].name}
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop vertical stepper - hidden on mobile */}
            <div className="col-lg-3 col-md-4 d-none d-md-block">
                {renderStepper()}
            </div>

            <div className="col-lg-9 col-md-8 col-12">
                <div className="card stretch stretch-full">

                    <div className="card-body">
                        {validationError && (
                            <div className="alert alert-warning mb-4">{validationError}</div>
                        )}
                        {submitError && (
                            <div className="alert alert-danger mb-4">{submitError}</div>
                        )}

                        <div className="wizard-content">
                            <CurrentStepComponent {...stepProps} />
                        </div>
                        
                        <SubmitConfirmModal 
                            show={showSubmitModal}
                            handleClose={() => setShowSubmitModal(false)}
                            handleConfirm={handleConfirmSubmit}
                        />

                        <ExcelUploadModal
                            show={showExcelModal}
                            handleClose={() => setShowExcelModal(false)}
                            branches={branches}
                            onSuccess={() => {
                                window.open('/invoices/list', '_self');
                            }}
                        />
                    </div>

                    <div className="card-footer d-flex flex-column-reverse flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2">
                        <button
                            className="btn btn-secondary w-100 w-sm-auto"
                            onClick={handlePrevious}
                            disabled={currentStep === 0 || isSubmitting}
                            style={{ minWidth: 'fit-content' }}
                        >
                            <i className="feather-chevron-right me-2"></i> مرحله قبلی
                        </button>

                        {currentStep < steps.length - 1 ? (
                            <button
                                className="btn btn-primary w-100 w-sm-auto"
                                onClick={handleNext}
                                disabled={isSubmitting}
                                style={{ minWidth: 'fit-content' }}
                            >
                                مرحله بعدی <i className="feather-chevron-left ms-2"></i>
                            </button>
                        ) : (
                            !readOnly && (
                                <div className='d-flex flex-column flex-sm-row gap-2'>
                                    <button
                                        className="btn btn-success w-100 w-sm-auto"
                                        onClick={() => handleSubmit(false)}
                                        disabled={isSubmitting}
                                        style={{ minWidth: 'fit-content' }}
                                    >
                                        {isSubmitting ? 'در حال ثبت...' : (mode === 'edit' ? 'ذخیره تغییرات' : 'ثبت')}
                                    </button>
                                    <button
                                        className="btn btn-dark w-100 w-sm-auto"
                                        onClick={() => handleSubmit(true)}
                                        disabled={isSubmitting}
                                        style={{ minWidth: 'fit-content' }}
                                    >
                                        {isSubmitting ? 'در حال ارسال و خروج...' : (mode === 'edit' ? 'ذخیره و خروج' : 'ثبت و خروج')}
                                    </button>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
            </div>
            {toast.show && (
                <div className="position-fixed p-3" style={{ top: 20, left: 20, zIndex: 9999 }}>
                    <div className={`toast show bg-${toast.type === 'success' ? 'success' : 'danger'} text-white`} role="alert">
                        <div className="toast-header" style={{ direction: 'rtl' }}>
                            <strong className="me-auto">{toast.type === 'success' ? 'موفق' : 'خطا'}</strong>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setToast({ ...toast, show: false })}></button>
                        </div>
                        <div className="toast-body">
                            {toast.message}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default InvoiceWizard;